import React, { useEffect, useState } from "react";
import {
    Card,
    Button,
    Table,
    Modal,
    Form,
    Input,
    DatePicker,
    Upload,
    Select,
    message,
    Row,
    Col,
    Typography,
    Divider,
} from "antd";
import { PlusOutlined, UploadOutlined, DeleteOutlined } from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import "../../../config";

const { Title } = Typography;
const { Option } = Select;

const NewLaunch = () => {
    const [launches, setLaunches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState([]);

    // Product Selection State
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedProducts, setSelectedProducts] = useState([]); // Array of product objects
    const [productLoading, setProductLoading] = useState(false);

    const [isEditMode, setIsEditMode] = useState(false);
    const [currentLaunchId, setCurrentLaunchId] = useState(null);

    useEffect(() => {
        document.title = "JOJ | New Launch";
        checkUserActiveStatus();
        fetchLaunches();
        fetchCategories();
    }, []);

    const checkUserActiveStatus = async () => {
        const tokenString = localStorage.getItem("token");
        try {
            let config = {
                method: "get",
                url: global.config.APIURL.BaseURL + "api/UserAccount/GetUserAccountDetail",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + tokenString,
                },
            };
            const response = await axios.request(config);
            if (
                response.data.IsActive === false ||
                response.data.IsDeleted === true ||
                response.data.IsEmployee !== true
            ) {
                alert("You are not authorized to access this page.");
                localStorage.removeItem("token");
                window.location.href = "/";
            }
        } catch (error) {
            if (error.response && error.response.status === 401) {
                // Token expired or invalid
                localStorage.removeItem("token");
                window.location.href = "/login";
            }
        }
    };

    const fetchLaunches = async () => {
        setLoading(true);
        try {
            const tokenString = localStorage.getItem("token");
            const config = {
                method: "get",
                url: global.config.APIURL.BaseURL + "api/NewLaunch/GetNewLaunch",
                headers: {
                    Authorization: "Bearer " + tokenString,
                },
            };
            const response = await axios.request(config);
            setLaunches(response.data || []);
        } catch (error) {
            console.error("Error fetching launches:", error);
            // message.error("Failed to fetch launches."); // API might return 404 if empty?
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const tokenString = localStorage.getItem("token");
            const listData = { FirmID: "1" };
            const config = {
                method: "get",
                url: global.config.APIURL.BaseURL + "api/ProductCategory/ListofProductCategory",
                headers: {
                    Authorization: "Bearer " + tokenString,
                },
                data: listData,
            };
            const response = await axios.request(config);
            if (Array.isArray(response.data)) {
                const flatCategories = response.data.flatMap((cat) => cat.data || []);
                flatCategories.sort((a, b) => a.CategoryName.localeCompare(b.CategoryName));
                setCategories(flatCategories);
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
    };

    const fetchProducts = async (categoryID) => {
        setProductLoading(true);
        try {
            const tokenString = localStorage.getItem("token");
            const config = {
                method: "get",
                url:
                    global.config.APIURL.BaseURL +
                    "api/Product/ListofProductByCategoryforList?CategoryID=" +
                    categoryID,
                headers: {
                    Authorization: "Bearer " + tokenString,
                },
            };
            const response = await axios.request(config);
            setProducts(response.data || []);
        } catch (error) {
            console.error("Error fetching products:", error);
            message.error("Failed to fetch products.");
        } finally {
            setProductLoading(false);
        }
    };

    const handleCategoryChange = (value) => {
        setSelectedCategory(value);
        fetchProducts(value);
    };

    const handleProductSelect = (value, option) => {
        // Check if product already selected
        if (!selectedProducts.find((p) => p.ProductID === value)) {
            const product = products.find((p) => p.ProductID === value);
            if (product) {
                setSelectedProducts([...selectedProducts, product]);
            }
        }
    };

    const handleRemoveProduct = (productId) => {
        setSelectedProducts(selectedProducts.filter((p) => p.ProductID !== productId));
    };

    const handleEditLaunch = (record) => {
        setIsEditMode(true);
        setCurrentLaunchId(record.LaunchID);

        // Populate form
        form.setFieldsValue({
            LaunchTitle: record.LaunchTitle,
            LaunchDate: moment(record.LaunchDate),
        });

        // Set products
        // Handle different casing/property names just in case
        const existingProducts = record.Products || record.products || record.NewLaunchProds || [];
        setSelectedProducts(existingProducts);

        // For image, we can't easily set the file object from URL, 
        // so we might need to handle it differently or just show the existing one.
        // For now, we'll leave the file list empty unless they upload a new one.
        setFileList([]);

        setIsModalVisible(true);
    };

    const handleCreateOrUpdateLaunch = async (values) => {
        if (!isEditMode && fileList.length === 0) {
            message.error("Please upload a Hero Image.");
            return;
        }
        if (selectedProducts.length === 0) {
            message.error("Please select at least one product.");
            return;
        }

        if (isEditMode) {
            message.info("Update API implementation pending. Visual check only.");
            console.log("Update Data:", {
                LaunchID: currentLaunchId,
                LaunchTitle: values.LaunchTitle,
                LaunchDate: values.LaunchDate.format("DD-MM-YYYY HH:mm:ss"),
                Products: selectedProducts,
                NewImage: fileList.length > 0 ? fileList[0] : "No new image"
            });
            // setIsModalVisible(false); // Keep open or close? User said "then will implement backed part"
            return;
        }

        setLoading(true);
        try {
            const tokenString = localStorage.getItem("token");

            const launchData = {
                LaunchDate: values.LaunchDate.format("DD-MM-YYYY HH:mm:ss"),
                LaunchTitle: values.LaunchTitle,
                NewLaunchProds: selectedProducts.map((prod, index) => ({
                    ProductID: prod.ProductID,
                    SortOrder: index + 1,
                })),
            };

            const formData = new FormData();
            formData.append("launchData", JSON.stringify(launchData));
            formData.append("HeroImage", fileList[0]);

            const config = {
                method: "post",
                url: global.config.APIURL.BaseURL + "api/NewLaunch/InsertNewLaunch",
                headers: {
                    Authorization: "Bearer " + tokenString,
                },
                data: formData,
            };

            await axios.request(config);
            message.success("New Launch created successfully!");
            setIsModalVisible(false);
            form.resetFields();
            setFileList([]);
            setSelectedProducts([]);
            setSelectedCategory(null);
            fetchLaunches();
        } catch (error) {
            console.error("Error creating launch:", error);
            message.error("Failed to create new launch.");
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: "Launch ID",
            dataIndex: "LaunchID",
            key: "LaunchID",
        },
        {
            title: "Title",
            dataIndex: "LaunchTitle",
            key: "LaunchTitle",
        },
        {
            title: "Launch Date",
            dataIndex: "LaunchDate",
            key: "LaunchDate",
            render: (text) => moment(text).format("DD-MM-YYYY"),
        },
        {
            title: "Hero Image",
            dataIndex: "HeroImageURL",
            key: "HeroImageURL",
            render: (url) => (
                <img
                    src={url}
                    alt="Hero"
                    style={{ width: 100, height: 60, objectFit: "cover", borderRadius: 4 }}
                />
            ),
        },
        {
            title: "Products",
            key: "Products",
            render: (text, record) => {
                console.log("Launch Record:", record); // Debugging log
                const products = record.Products || record.products || record.NewLaunchProds;
                return products ? products.length : 0;
            },
        },
        {
            title: "Action",
            key: "action",
            render: (text, record) => (
                <Button type="primary" onClick={() => handleEditLaunch(record)}>
                    Modify
                </Button>
            ),
        },
    ];

    const uploadProps = {
        onRemove: (file) => {
            setFileList([]);
        },
        beforeUpload: (file) => {
            const isLt4M = file.size / 1024 / 1024 < 4;
            if (!isLt4M) {
                message.error("Image must be smaller than 4MB!");
                return Upload.LIST_IGNORE;
            }
            setFileList([file]);
            return false;
        },
        fileList,
    };

    return (
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "20px" }}>
            <Card>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                    <Title level={2} style={{ margin: 0 }}>Manage New Launches</Title>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setIsEditMode(false);
                            setCurrentLaunchId(null);
                            form.resetFields();
                            setFileList([]);
                            setSelectedProducts([]);
                            setIsModalVisible(true);
                        }}
                    >
                        Create New Launch
                    </Button>
                </div>

                <Table
                    columns={columns}
                    dataSource={launches}
                    rowKey="LaunchID"
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                />
            </Card>

            <Modal
                title={isEditMode ? "Modify Launch" : "Create New Launch"}
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                footer={null}
                width={800}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleCreateOrUpdateLaunch}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="LaunchTitle"
                                label="Launch Title"
                                rules={[{ required: true, message: "Please enter launch title" }]}
                            >
                                <Input placeholder="Enter title" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="LaunchDate"
                                label="Launch Date"
                                rules={[{ required: true, message: "Please select launch date" }]}
                            >
                                <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item label="Hero Image" required={!isEditMode}>
                        <Upload {...uploadProps} listType="picture" maxCount={1}>
                            <Button icon={<UploadOutlined />}>
                                {isEditMode ? "Change Image" : "Select Image"}
                            </Button>
                        </Upload>
                        {isEditMode && fileList.length === 0 && (
                            <div style={{ marginTop: 8, color: "#888" }}>
                                (Keep existing image if not changed)
                            </div>
                        )}
                    </Form.Item>

                    <Divider orientation="left">Add Products</Divider>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="Select Category">
                                <Select
                                    placeholder="Select a category"
                                    onChange={handleCategoryChange}
                                    value={selectedCategory}
                                    showSearch
                                    optionFilterProp="children"
                                >
                                    {categories.map((cat) => (
                                        <Option key={cat.ProductCategoryID} value={cat.ProductCategoryID}>
                                            {cat.CategoryName}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Select Product">
                                <Select
                                    placeholder="Select product to add"
                                    onChange={handleProductSelect}
                                    loading={productLoading}
                                    showSearch
                                    optionFilterProp="children"
                                    disabled={!selectedCategory}
                                    value={null} // Reset value after selection
                                >
                                    {products.map((prod) => (
                                        <Option key={prod.ProductID} value={prod.ProductID}>
                                            {prod.ProductName} ({prod.DesignNo})
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Selected Products List */}
                    <div style={{ marginBottom: 24, border: "1px solid #f0f0f0", borderRadius: 4, padding: 8, maxHeight: 300, overflowY: "auto" }}>
                        {selectedProducts.length === 0 ? (
                            <div style={{ textAlign: "center", color: "#999", padding: 20 }}>
                                No products selected yet.
                            </div>
                        ) : (
                            selectedProducts.map((prod, index) => (
                                <div
                                    key={prod.ProductID}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        padding: "8px 0",
                                        borderBottom: "1px solid #f0f0f0",
                                    }}
                                >
                                    <span style={{ marginRight: 16, fontWeight: "bold", color: "#888" }}>
                                        #{index + 1}
                                    </span>
                                    <img
                                        src={prod.ImageUrl}
                                        alt={prod.ProductName}
                                        style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 4, marginRight: 12 }}
                                    />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 500 }}>{prod.ProductName}</div>
                                        <div style={{ fontSize: 12, color: "#888" }}>{prod.DesignNo}</div>
                                    </div>
                                    <Button
                                        type="text"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => handleRemoveProduct(prod.ProductID)}
                                    />
                                </div>
                            ))
                        )}
                    </div>

                    <Form.Item>
                        <Button type="primary" htmlType="submit" block loading={loading}>
                            {isEditMode ? "Save Changes (Visual Only)" : "Create Launch"}
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default NewLaunch;
